/**
 * Region catalog (admin UI mirror of backend's src/ecommerce/region-catalog.ts).
 *
 * Duplicated intentionally — projects-admin and aapastech-backend are separate
 * builds without cross-project module sharing wired up. Keep this in sync with
 * the backend file when adding countries.
 *
 * Store subdivision code WITHOUT the country prefix — "DL" not "IN-DL".
 */

export interface RegionEntry {
  code: string;
  name: string;
}

export interface CountryCatalog {
  country: string; // ISO 3166-1 alpha-2
  name: string;
  flag: string; // emoji for display
  regions: RegionEntry[];
}

const IN: RegionEntry[] = [
  { code: 'AN', name: 'Andaman and Nicobar Islands' },
  { code: 'AP', name: 'Andhra Pradesh' },
  { code: 'AR', name: 'Arunachal Pradesh' },
  { code: 'AS', name: 'Assam' },
  { code: 'BR', name: 'Bihar' },
  { code: 'CH', name: 'Chandigarh' },
  { code: 'CT', name: 'Chhattisgarh' },
  { code: 'DN', name: 'Dadra and Nagar Haveli and Daman and Diu' },
  { code: 'DL', name: 'Delhi' },
  { code: 'GA', name: 'Goa' },
  { code: 'GJ', name: 'Gujarat' },
  { code: 'HR', name: 'Haryana' },
  { code: 'HP', name: 'Himachal Pradesh' },
  { code: 'JK', name: 'Jammu and Kashmir' },
  { code: 'JH', name: 'Jharkhand' },
  { code: 'KA', name: 'Karnataka' },
  { code: 'KL', name: 'Kerala' },
  { code: 'LA', name: 'Ladakh' },
  { code: 'LD', name: 'Lakshadweep' },
  { code: 'MP', name: 'Madhya Pradesh' },
  { code: 'MH', name: 'Maharashtra' },
  { code: 'MN', name: 'Manipur' },
  { code: 'ML', name: 'Meghalaya' },
  { code: 'MZ', name: 'Mizoram' },
  { code: 'NL', name: 'Nagaland' },
  { code: 'OR', name: 'Odisha' },
  { code: 'PY', name: 'Puducherry' },
  { code: 'PB', name: 'Punjab' },
  { code: 'RJ', name: 'Rajasthan' },
  { code: 'SK', name: 'Sikkim' },
  { code: 'TN', name: 'Tamil Nadu' },
  { code: 'TG', name: 'Telangana' },
  { code: 'TR', name: 'Tripura' },
  { code: 'UP', name: 'Uttar Pradesh' },
  { code: 'UT', name: 'Uttarakhand' },
  { code: 'WB', name: 'West Bengal' },
];

const CA: RegionEntry[] = [
  { code: 'AB', name: 'Alberta' },
  { code: 'BC', name: 'British Columbia' },
  { code: 'MB', name: 'Manitoba' },
  { code: 'NB', name: 'New Brunswick' },
  { code: 'NL', name: 'Newfoundland and Labrador' },
  { code: 'NS', name: 'Nova Scotia' },
  { code: 'NT', name: 'Northwest Territories' },
  { code: 'NU', name: 'Nunavut' },
  { code: 'ON', name: 'Ontario' },
  { code: 'PE', name: 'Prince Edward Island' },
  { code: 'QC', name: 'Quebec' },
  { code: 'SK', name: 'Saskatchewan' },
  { code: 'YT', name: 'Yukon' },
];

const US: RegionEntry[] = [
  { code: 'AL', name: 'Alabama' },
  { code: 'AK', name: 'Alaska' },
  { code: 'AZ', name: 'Arizona' },
  { code: 'AR', name: 'Arkansas' },
  { code: 'CA', name: 'California' },
  { code: 'CO', name: 'Colorado' },
  { code: 'CT', name: 'Connecticut' },
  { code: 'DE', name: 'Delaware' },
  { code: 'DC', name: 'District of Columbia' },
  { code: 'FL', name: 'Florida' },
  { code: 'GA', name: 'Georgia' },
  { code: 'HI', name: 'Hawaii' },
  { code: 'ID', name: 'Idaho' },
  { code: 'IL', name: 'Illinois' },
  { code: 'IN', name: 'Indiana' },
  { code: 'IA', name: 'Iowa' },
  { code: 'KS', name: 'Kansas' },
  { code: 'KY', name: 'Kentucky' },
  { code: 'LA', name: 'Louisiana' },
  { code: 'ME', name: 'Maine' },
  { code: 'MD', name: 'Maryland' },
  { code: 'MA', name: 'Massachusetts' },
  { code: 'MI', name: 'Michigan' },
  { code: 'MN', name: 'Minnesota' },
  { code: 'MS', name: 'Mississippi' },
  { code: 'MO', name: 'Missouri' },
  { code: 'MT', name: 'Montana' },
  { code: 'NE', name: 'Nebraska' },
  { code: 'NV', name: 'Nevada' },
  { code: 'NH', name: 'New Hampshire' },
  { code: 'NJ', name: 'New Jersey' },
  { code: 'NM', name: 'New Mexico' },
  { code: 'NY', name: 'New York' },
  { code: 'NC', name: 'North Carolina' },
  { code: 'ND', name: 'North Dakota' },
  { code: 'OH', name: 'Ohio' },
  { code: 'OK', name: 'Oklahoma' },
  { code: 'OR', name: 'Oregon' },
  { code: 'PA', name: 'Pennsylvania' },
  { code: 'RI', name: 'Rhode Island' },
  { code: 'SC', name: 'South Carolina' },
  { code: 'SD', name: 'South Dakota' },
  { code: 'TN', name: 'Tennessee' },
  { code: 'TX', name: 'Texas' },
  { code: 'UT', name: 'Utah' },
  { code: 'VT', name: 'Vermont' },
  { code: 'VA', name: 'Virginia' },
  { code: 'WA', name: 'Washington' },
  { code: 'WV', name: 'West Virginia' },
  { code: 'WI', name: 'Wisconsin' },
  { code: 'WY', name: 'Wyoming' },
  { code: 'AS', name: 'American Samoa' },
  { code: 'GU', name: 'Guam' },
  { code: 'MP', name: 'Northern Mariana Islands' },
  { code: 'PR', name: 'Puerto Rico' },
  { code: 'VI', name: 'U.S. Virgin Islands' },
];

const CN: RegionEntry[] = [
  { code: 'AH', name: 'Anhui' },
  { code: 'BJ', name: 'Beijing' },
  { code: 'CQ', name: 'Chongqing' },
  { code: 'FJ', name: 'Fujian' },
  { code: 'GS', name: 'Gansu' },
  { code: 'GD', name: 'Guangdong' },
  { code: 'GX', name: 'Guangxi' },
  { code: 'GZ', name: 'Guizhou' },
  { code: 'HI', name: 'Hainan' },
  { code: 'HE', name: 'Hebei' },
  { code: 'HL', name: 'Heilongjiang' },
  { code: 'HA', name: 'Henan' },
  { code: 'HB', name: 'Hubei' },
  { code: 'HN', name: 'Hunan' },
  { code: 'JS', name: 'Jiangsu' },
  { code: 'JX', name: 'Jiangxi' },
  { code: 'JL', name: 'Jilin' },
  { code: 'LN', name: 'Liaoning' },
  { code: 'NM', name: 'Nei Mongol (Inner Mongolia)' },
  { code: 'NX', name: 'Ningxia' },
  { code: 'QH', name: 'Qinghai' },
  { code: 'SN', name: 'Shaanxi' },
  { code: 'SD', name: 'Shandong' },
  { code: 'SH', name: 'Shanghai' },
  { code: 'SX', name: 'Shanxi' },
  { code: 'SC', name: 'Sichuan' },
  { code: 'TJ', name: 'Tianjin' },
  { code: 'XJ', name: 'Xinjiang' },
  { code: 'XZ', name: 'Xizang (Tibet)' },
  { code: 'YN', name: 'Yunnan' },
  { code: 'ZJ', name: 'Zhejiang' },
  { code: 'HK', name: 'Hong Kong' },
  { code: 'MO', name: 'Macao' },
  { code: 'TW', name: 'Taiwan' },
];

const GB: RegionEntry[] = [
  { code: 'ENG', name: 'England' },
  { code: 'NIR', name: 'Northern Ireland' },
  { code: 'SCT', name: 'Scotland' },
  { code: 'WLS', name: 'Wales' },
];

const AU: RegionEntry[] = [
  { code: 'ACT', name: 'Australian Capital Territory' },
  { code: 'NSW', name: 'New South Wales' },
  { code: 'NT', name: 'Northern Territory' },
  { code: 'QLD', name: 'Queensland' },
  { code: 'SA', name: 'South Australia' },
  { code: 'TAS', name: 'Tasmania' },
  { code: 'VIC', name: 'Victoria' },
  { code: 'WA', name: 'Western Australia' },
];

const DE: RegionEntry[] = [
  { code: 'BW', name: 'Baden-Württemberg' },
  { code: 'BY', name: 'Bavaria' },
  { code: 'BE', name: 'Berlin' },
  { code: 'BB', name: 'Brandenburg' },
  { code: 'HB', name: 'Bremen' },
  { code: 'HH', name: 'Hamburg' },
  { code: 'HE', name: 'Hesse' },
  { code: 'MV', name: 'Mecklenburg-Vorpommern' },
  { code: 'NI', name: 'Lower Saxony' },
  { code: 'NW', name: 'North Rhine-Westphalia' },
  { code: 'RP', name: 'Rhineland-Palatinate' },
  { code: 'SL', name: 'Saarland' },
  { code: 'SN', name: 'Saxony' },
  { code: 'ST', name: 'Saxony-Anhalt' },
  { code: 'SH', name: 'Schleswig-Holstein' },
  { code: 'TH', name: 'Thuringia' },
];

const FR: RegionEntry[] = [
  { code: 'ARA', name: 'Auvergne-Rhône-Alpes' },
  { code: 'BFC', name: 'Bourgogne-Franche-Comté' },
  { code: 'BRE', name: 'Brittany' },
  { code: 'CVL', name: 'Centre-Val de Loire' },
  { code: 'COR', name: 'Corsica' },
  { code: 'GES', name: 'Grand Est' },
  { code: 'HDF', name: 'Hauts-de-France' },
  { code: 'IDF', name: 'Île-de-France' },
  { code: 'NOR', name: 'Normandy' },
  { code: 'NAQ', name: 'Nouvelle-Aquitaine' },
  { code: 'OCC', name: 'Occitania' },
  { code: 'PDL', name: 'Pays de la Loire' },
  { code: 'PAC', name: "Provence-Alpes-Côte d'Azur" },
];

const AE: RegionEntry[] = [
  { code: 'AZ', name: 'Abu Dhabi' },
  { code: 'AJ', name: 'Ajman' },
  { code: 'DU', name: 'Dubai' },
  { code: 'FU', name: 'Fujairah' },
  { code: 'RK', name: 'Ras al-Khaimah' },
  { code: 'SH', name: 'Sharjah' },
  { code: 'UQ', name: 'Umm al-Quwain' },
];

const SG: RegionEntry[] = [{ code: 'SG', name: 'Singapore' }];

export const CATALOGS: Record<string, CountryCatalog> = {
  IN: { country: 'IN', name: 'India', flag: '🇮🇳', regions: IN },
  CA: { country: 'CA', name: 'Canada', flag: '🇨🇦', regions: CA },
  US: { country: 'US', name: 'United States', flag: '🇺🇸', regions: US },
  CN: { country: 'CN', name: 'China', flag: '🇨🇳', regions: CN },
  GB: { country: 'GB', name: 'United Kingdom', flag: '🇬🇧', regions: GB },
  AU: { country: 'AU', name: 'Australia', flag: '🇦🇺', regions: AU },
  DE: { country: 'DE', name: 'Germany', flag: '🇩🇪', regions: DE },
  FR: { country: 'FR', name: 'France', flag: '🇫🇷', regions: FR },
  AE: { country: 'AE', name: 'United Arab Emirates', flag: '🇦🇪', regions: AE },
  SG: { country: 'SG', name: 'Singapore', flag: '🇸🇬', regions: SG },
};

// Common non-catalog destinations that admins ship to.
// Code + flag only — no region breakdown.
export const COMMON_OTHER_COUNTRIES: Array<{ country: string; name: string; flag: string }> = [
  { country: 'MX', name: 'Mexico', flag: '🇲🇽' },
  { country: 'BR', name: 'Brazil', flag: '🇧🇷' },
  { country: 'JP', name: 'Japan', flag: '🇯🇵' },
  { country: 'KR', name: 'South Korea', flag: '🇰🇷' },
  { country: 'NZ', name: 'New Zealand', flag: '🇳🇿' },
  { country: 'ZA', name: 'South Africa', flag: '🇿🇦' },
  { country: 'IT', name: 'Italy', flag: '🇮🇹' },
  { country: 'ES', name: 'Spain', flag: '🇪🇸' },
  { country: 'NL', name: 'Netherlands', flag: '🇳🇱' },
  { country: 'BE', name: 'Belgium', flag: '🇧🇪' },
  { country: 'SE', name: 'Sweden', flag: '🇸🇪' },
  { country: 'NO', name: 'Norway', flag: '🇳🇴' },
  { country: 'DK', name: 'Denmark', flag: '🇩🇰' },
  { country: 'FI', name: 'Finland', flag: '🇫🇮' },
  { country: 'PL', name: 'Poland', flag: '🇵🇱' },
  { country: 'CH', name: 'Switzerland', flag: '🇨🇭' },
  { country: 'AT', name: 'Austria', flag: '🇦🇹' },
  { country: 'IE', name: 'Ireland', flag: '🇮🇪' },
  { country: 'PT', name: 'Portugal', flag: '🇵🇹' },
];

/** All supported countries (cataloged + common others), sorted by name. */
export function getAllCountries(): Array<{ country: string; name: string; flag: string }> {
  const cataloged = Object.values(CATALOGS).map((c) => ({
    country: c.country,
    name: c.name,
    flag: c.flag,
  }));
  return [...cataloged, ...COMMON_OTHER_COUNTRIES].sort((a, b) => a.name.localeCompare(b.name));
}

/** Regions for a country, or empty array if not cataloged. */
export function getRegionsForCountry(country: string): RegionEntry[] {
  return CATALOGS[country.toUpperCase()]?.regions ?? [];
}

/** Look up a country's display metadata. */
export function getCountryMeta(
  country: string
): { country: string; name: string; flag: string } | null {
  const cc = country.toUpperCase();
  if (CATALOGS[cc]) {
    return { country: CATALOGS[cc].country, name: CATALOGS[cc].name, flag: CATALOGS[cc].flag };
  }
  const other = COMMON_OTHER_COUNTRIES.find((c) => c.country === cc);
  return other ?? null;
}

/** Translate codes back to names (for display). */
export function regionCodesToNames(country: string, codes: string[]): string[] {
  const regions = getRegionsForCountry(country);
  const byCode = new Map(regions.map((r) => [r.code, r.name]));
  return codes.map((c) => byCode.get(c) ?? c);
}
